/** The two rect fields the strip math needs — a `DOMRect` satisfies it. */
export interface HorizontalRect {
  left: number;
  width: number;
}

/** Breathing room kept on either side of the tab brought into view (Shell.jsx `pad`). */
export const SCROLL_INTO_VIEW_PAD = 14;

/**
 * `scrollLeft` a horizontally scrolling strip needs so that `active` is fully visible with `pad`
 * on either side — port of the prototype's Shell.jsx layout effect (lines 70–78), on rects
 * instead of `offsetLeft` so it is independent of the offset parent.
 *
 * Only the strip's own `scrollLeft` moves: never `element.scrollIntoView()`, which would also
 * scroll the page vertically (the tab nav is sticky under the top bar). Returns `currentScroll`
 * unchanged when the tab already fits; when the tab is wider than the strip its start wins.
 * The result is not clamped to the scroll range — the browser does that on assignment, and the
 * unclamped value keeps the maths direction-agnostic (RTL strips have a negative range).
 */
export function computeScrollLeft(
  navRect: HorizontalRect,
  activeRect: HorizontalRect,
  currentScroll: number,
  pad: number = SCROLL_INTO_VIEW_PAD,
): number {
  // Position of the tab inside the scroll content (what offsetLeft would be in the strip).
  const start = activeRect.left - navRect.left + currentScroll;
  const end = start + activeRect.width;
  if (start - pad < currentScroll) return start - pad;
  if (end + pad > currentScroll + navRect.width) return end + pad - navRect.width;
  return currentScroll;
}
